import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateWalkInVisitaRequestSchema } from '@condo/contracts';
import type { CreateWalkInVisitaRequest } from '@condo/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUnidades } from '@/features/unidades/api';
import { useRegistrarWalkIn } from './api';

interface Props {
  condoId: string;
  open: boolean;
  onClose: () => void;
}

export function WalkInModal({ condoId, open, onClose }: Props) {
  const form = useForm<CreateWalkInVisitaRequest>({
    resolver: zodResolver(CreateWalkInVisitaRequestSchema),
  });
  const { mutate, isPending } = useRegistrarWalkIn(condoId);
  const { data: unidadesData } = useUnidades(condoId, { activo: true });
  const unidades = unidadesData?.items ?? [];

  const onSubmit = (data: CreateWalkInVisitaRequest) => {
    mutate(data, {
      onSuccess: () => {
        form.reset();
        onClose();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar visita walk-in</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-2">
          <div>
            <label className="block text-sm mb-1 text-[var(--color-ink-muted)]">
              Unidad
            </label>
            <select
              {...form.register('unidadId')}
              className="w-full rounded-md border border-[var(--color-hairline)] px-3 py-2 text-sm bg-[var(--color-paper)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="">Seleccionar unidad…</option>
              {unidades.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.numero}
                  {u.piso !== undefined ? ` · Piso ${u.piso}` : ''}
                </option>
              ))}
            </select>
            {form.formState.errors.unidadId && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.unidadId.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-1 text-[var(--color-ink-muted)]">
              Nombre del visitante
            </label>
            <Input {...form.register('visitorNombre')} placeholder="Nombre completo" />
            {form.formState.errors.visitorNombre && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.visitorNombre.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-1 text-[var(--color-ink-muted)]">
              DNI / RUN
            </label>
            <Input {...form.register('visitorDni')} placeholder="12.345.678-9" />
            {form.formState.errors.visitorDni && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.visitorDni.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-1 text-[var(--color-ink-muted)]">
              Tipo
            </label>
            <select
              {...form.register('tipo')}
              className="w-full rounded-md border border-[var(--color-hairline)] px-3 py-2 text-sm bg-[var(--color-paper)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="familiar">Familiar</option>
              <option value="delivery">Delivery</option>
              <option value="proveedor">Proveedor</option>
              <option value="servicio">Servicio</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1 text-[var(--color-ink-muted)]">
              Patente vehículo{' '}
              <span className="text-[var(--color-ink-muted)] font-normal">
                (opcional)
              </span>
            </label>
            <Input
              {...form.register('vehiclePatent')}
              placeholder="AABB12"
              className="uppercase"
            />
            {form.formState.errors.vehiclePatent && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.vehiclePatent.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-1 text-[var(--color-ink-muted)]">
              Observaciones{' '}
              <span className="text-[var(--color-ink-muted)] font-normal">
                (opcional)
              </span>
            </label>
            <Input
              {...form.register('observaciones')}
              placeholder="Ej: trae encomienda"
            />
            {form.formState.errors.observaciones && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {form.formState.errors.observaciones.message}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Registrando…' : 'Registrar ingreso'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
